<template>
  <Teleport to="body">
    <div v-if="visible" class="login-overlay" @click.self="$emit('close')">
      <div class="login-modal">
        <div class="login-header">
          <h2 class="login-title">AIGC</h2>
          <p class="login-subtitle">
            {{ mode === "login" ? "登录你的账号" : mode === "register" ? "创建新账号" : "重置密码" }}
          </p>
        </div>

        <div class="login-form">
          <div class="form-group">
            <label>账号</label>
            <input
              v-model="account"
              type="text"
              placeholder="请输入账号"
              @keyup.enter="handleSubmit"
            />
          </div>

          <div class="form-group">
            <label>密码</label>
            <input
              v-model="password"
              type="password"
              :placeholder="mode === 'reset' ? '请输入新密码' : '请输入密码'"
              @keyup.enter="handleSubmit"
            />
          </div>

          <div v-if="mode === 'reset'" class="form-group">
            <label>验证码</label>
            <input
              v-model="verifyCode"
              type="text"
              placeholder="请输入验证码"
              @keyup.enter="handleSubmit"
            />
          </div>

          <button class="submit-btn" :disabled="loading" @click="handleSubmit">
            {{ loading ? "处理中..." : submitText }}
          </button>

          <p v-if="error" class="error-msg">{{ error }}</p>
        </div>

        <div class="login-footer">
          <template v-if="mode === 'login'">
            <a @click="mode = 'register'">没有账号？注册</a>
            <a @click="mode = 'reset'">忘记密码？</a>
          </template>
          <template v-else>
            <a @click="mode = 'login'">已有账号？登录</a>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import {
  loginApi,
  registerApi,
  resetPasswordApi,
  setToken,
  setAccount,
  setRole,
} from "../../services/auth";

type Mode = "login" | "register" | "reset";

export default defineComponent({
  name: "LoginModal",
  props: {
    visible: { type: Boolean, default: false },
  },
  emits: ["close", "success"],
  setup(props, { emit }) {
    const mode = ref<Mode>("login");
    const account = ref("");
    const password = ref("");
    const verifyCode = ref("");
    const loading = ref(false);
    const error = ref("");

    const submitText = computed(() => {
      if (mode.value === "login") return "登录";
      if (mode.value === "register") return "注册";
      return "重置密码";
    });

    const handleSubmit = async () => {
      error.value = "";
      if (!account.value || !password.value) {
        error.value = "请输入账号和密码";
        return;
      }
      if (mode.value === "reset" && !verifyCode.value) {
        error.value = "请输入验证码";
        return;
      }

      loading.value = true;
      try {
        if (mode.value === "register") {
          await registerApi(account.value, password.value);
          mode.value = "login";
          error.value = "";
          alert("注册成功，请登录");
        } else if (mode.value === "reset") {
          await resetPasswordApi(
            account.value,
            password.value,
            verifyCode.value
          );
          mode.value = "login";
          error.value = "";
          alert("密码重置成功，请登录");
        } else {
          const data = await loginApi(account.value, password.value);
          setToken(data.token);
          setAccount(data.account);
          setRole(data.role);
          emit("success", data);
        }
      } catch (e: any) {
        error.value = e.message || "操作失败";
      } finally {
        loading.value = false;
      }
    };

    return {
      mode,
      account,
      password,
      verifyCode,
      loading,
      error,
      submitText,
      handleSubmit,
    };
  },
});
</script>

<style lang="less" scoped>
@import "../../assets/styles/variables.less";

.login-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.login-modal {
  width: 400px;
  max-width: 90vw;
  padding: 40px;
  background: @login-modal-bg;
  border-radius: @login-modal-radius;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.login-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.login-subtitle {
  color: #666;
  font-size: 14px;
}

.login-form {
  .form-group {
    margin-bottom: 20px;

    label {
      display: block;
      font-size: 13px;
      font-weight: 500;
      color: #444;
      margin-bottom: 8px;
    }

    input {
      width: 100%;
      padding: 10px 14px;
      background: #f5f5f5;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      color: #333;
      font-size: 14px;
      outline: none;
      transition: border-color 0.2s;
      box-sizing: border-box;

      &::placeholder {
        color: #aaa;
      }

      &:focus {
        border-color: @login-btn-bg;
      }
    }
  }
}

.submit-btn {
  width: 100%;
  padding: 12px;
  background: @login-btn-bg;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: @login-btn-hover;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.error-msg {
  color: #ef4444;
  font-size: 13px;
  margin-top: 12px;
  text-align: center;
}

.login-footer {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 24px;

  a {
    color: #888;
    font-size: 13px;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: @login-btn-bg;
    }
  }
}
</style>
